"use client";

import {
  m,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";
import { useEffect, useRef } from "react";

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  className?: string;
  formatValue?: (value: number) => string;
}

export function AnimatedCounter({
  value,
  duration = 2,
  className,
  formatValue = (v) => Math.round(v).toLocaleString("pl-PL"),
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: 50,
    stiffness: 100,
    duration: duration * 1000,
  });
  const displayValue = useTransform(springValue, (latest) =>
    formatValue(latest)
  );

  useEffect(() => {
    if (isInView && !prefersReducedMotion) {
      motionValue.set(value);
    }
  }, [isInView, motionValue, prefersReducedMotion, value]);

  // Return static value for users who prefer reduced motion
  if (prefersReducedMotion) {
    return <span className={className}>{formatValue(value)}</span>;
  }

  return (
    <m.span ref={ref} className={className}>
      {displayValue}
    </m.span>
  );
}
